import { useMutation } from '@tanstack/react-query'
import useFilters from 'ext-ui/hooks/filters/filters'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import VirtualCardsAllCards from 'src/app/VirtualCardsAllCards/VirtualCardsAllCards'
import VirtualCardsNeedsApproval from 'src/app/VirtualCardsNeedsApproval/VirtualCardsNeedsApproval'
import FamilyLayout, {
  FamilyLayoutTab,
} from 'src/component-layout/FamilyLayout/FamilyLayout'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import {
  makeBaseVirtualCardsSchema,
  VirtualCardsFilters,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  createVirtualCardUrl,
  requestVirtualCardUrl,
  virtualCardsAllCardsUrl,
  virtualCardsNeedsApprovalUrl,
  virtualCardsRequestsUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import { virtualCardsRouteTree } from 'src/navigation/virtualCardsRouteTree'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { reportDownloadsMutations } from 'src/queries/reportDownloads/reportDownloads.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { createSetIsDownloadSuccessPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectCalculatedBackgroundLocation,
  selectVirtualCardsAllCardsMatch,
  selectVirtualCardsNeedsApprovalMatch,
  selectVirtualCardsRequestsMatch,
} from 'src/redux/selectors/router.selectors'

// Stable test ids per family tab (preserve the legacy Subnav selectors so e2e /
// CI coverage keeps matching after the FamilyLayout swap).
const FAMILY_TAB_TEST_IDS: Record<string, string> = {
  allcards: 'AllCardsVirtualCards',
  needsapproval: 'RequestsNeedsApproval',
}

/**
 * VirtualCardsFamilyPage — route-level page for the Virtual Cards family tabs.
 * See src/page-components/AGENTS.md for parity notes vs. the legacy shell.
 */
const VirtualCardsFamilyPage: React.FC = () => {
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const virtualCardsAllCardsMatch = useSelector(selectVirtualCardsAllCardsMatch)
  const virtualCardsRequestsMatch = useSelector(selectVirtualCardsRequestsMatch)
  const virtualCardsNeedsApprovalMatch = useSelector(
    selectVirtualCardsNeedsApprovalMatch,
  )
  // CreditCardsCanIssueVirtualCardsCount was read here but never dispatched
  // by this page (legacy free-rode on other surfaces); it is now fetched
  // explicitly alongside the two keys the legacy mount effect dispatched.
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount,
      StatisticsKeys.CreditCardsUserOrViewerMeCount,
    ] as const,
  })
  const creditCardsCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsActiveCanIssueVirtualCardsCount =
    statisticsResults[StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]
      .data ?? 0
  const creditCardsUserOrViewerMeCount =
    statisticsResults[StatisticsKeys.CreditCardsUserOrViewerMeCount].data ?? 0
  const user = useCurrentUser()
  const isGuestRole = user?.organizationRole === OrganizationRoles.guest
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const reportVirtualCardsDownloadMutation = useMutation(
    reportDownloadsMutations.virtualCards(),
  )

  // Downloads always target the All Cards scope shown on this page's header
  // (the button is not rendered on My Wallet). Reuse the list query's own
  // schema/apiQueryParams mapping so the CSV filters can never drift from
  // what the list actually shows.
  const { apiQueryParams: filtersBody } = useFilters<
    VirtualCardsFilters,
    VirtualCardsFiltersBody
  >(
    useMemo(
      () =>
        makeBaseVirtualCardsSchema({
          context: VirtualCardsListContext.AllCards,
        }),
      [],
    ),
  )

  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const isManager = directReports !== undefined && directReports.length > 0

  const hasCreditCards = creditCardsUserOrViewerMeCount > 0
  const canIssueVirtualCard = creditCardsCanIssueVirtualCardsCount > 0
  const canCurrentlyIssueVirtualCard =
    creditCardsActiveCanIssueVirtualCardsCount > 0
  const hasAccessToAllCards =
    (hasCreditCards || user?.hasOrgBookkeeperPermissions || isManager) &&
    !isGuestRole

  const tabs: FamilyLayoutTab[] = useMemo(
    () =>
      virtualCardsRouteTree.family.tabs.map((tab) => ({
        key: tab.key,
        label: translate(tab.labelSource),
        to: tab.path,
        ariaLabelSource: tab.labelSource,
        testId: FAMILY_TAB_TEST_IDS[tab.key],
      })),
    [],
  )

  const activeTabKey = virtualCardsAllCardsMatch
    ? 'allcards'
    : virtualCardsRequestsMatch !== null ||
        virtualCardsNeedsApprovalMatch !== null
      ? 'needsapproval'
      : undefined

  // Only the tab URLs get the tab bar. Legacy computed an EMPTY Subnav for the
  // retired children, so they must render the shell tab-less.
  const tabbedPaths = useMemo(
    () => [
      ...virtualCardsRouteTree.family.tabs.map((tab) => tab.path),
      ...virtualCardsRouteTree.family.aliasPaths.map((alias) => alias.path),
    ],
    [],
  )

  const buttons = useMemo(
    () =>
      canIssueVirtualCard
        ? virtualCardsAllCardsMatch
          ? [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: translate('Download All Virtual Cards (CSV)'),
                disabled: reportVirtualCardsDownloadMutation.isPending,
                loading: reportVirtualCardsDownloadMutation.isPending,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  reportVirtualCardsDownloadMutation.mutate(filtersBody, {
                    onSuccess: () => {
                      dispatch(createSetIsDownloadSuccessPopupOpen(true))
                    },
                  })
                },
              },
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: translate('Request Card'),
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: translate('Create Card'),
                linkProps: {
                  to: canCurrentlyIssueVirtualCard
                    ? createVirtualCardUrl()
                    : actionUnavailableUrl(),
                  ariaLabel: 'Create Card',
                },
              },
            ]
          : [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: translate('Request Card'),
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: translate('Create Card'),
                linkProps: {
                  to: canCurrentlyIssueVirtualCard
                    ? createVirtualCardUrl()
                    : actionUnavailableUrl(),
                  ariaLabel: 'Create Card',
                },
              },
            ]
        : isGuestRole
          ? [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: translate('Download All Virtual Cards (CSV)'),
                disabled: reportVirtualCardsDownloadMutation.isPending,
                loading: reportVirtualCardsDownloadMutation.isPending,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  reportVirtualCardsDownloadMutation.mutate(filtersBody, {
                    onSuccess: () => {
                      dispatch(createSetIsDownloadSuccessPopupOpen(true))
                    },
                  })
                },
              },
            ]
          : [
              {
                design: ButtonDesigns.designCtaInverse,
                type: ButtonTypes.button,
                text: translate('Download All Virtual Cards (CSV)'),
                disabled: reportVirtualCardsDownloadMutation.isPending,
                loading: reportVirtualCardsDownloadMutation.isPending,
                onClick: (): void => {
                  analytics?.track(
                    'virtual cards - all cards - download all virtual cards',
                  )
                  reportVirtualCardsDownloadMutation.mutate(filtersBody, {
                    onSuccess: () => {
                      dispatch(createSetIsDownloadSuccessPopupOpen(true))
                    },
                  })
                },
              },
              {
                design: ButtonDesigns.designCta,
                type: ButtonTypes.button,
                text: translate('Request Card'),
                linkProps: {
                  to: requestVirtualCardUrl(),
                  ariaLabel: 'Request Card',
                },
              },
            ],
    [
      canIssueVirtualCard,
      canCurrentlyIssueVirtualCard,
      isGuestRole,
      filtersBody,
      virtualCardsAllCardsMatch,
      reportVirtualCardsDownloadMutation.isPending,
      reportVirtualCardsDownloadMutation.mutate,
    ],
  )

  return (
    <LayoutShell
      subnavOptions={[]}
      subnavDesign={SubnavDesign.Floating}
      title={translate('Virtual Cards')}
      buttons={buttons}
      content={
        <Switch location={calculatedBackgroundLocation}>
          <Route exact path={tabbedPaths}>
            <FamilyLayout tabs={tabs} activeTabKey={activeTabKey}>
              <Switch location={calculatedBackgroundLocation}>
                <Route exact path={virtualCardsAllCardsUrl()}>
                  <VirtualCardsAllCards />
                </Route>
                <Route exact path={virtualCardsNeedsApprovalUrl()}>
                  <VirtualCardsNeedsApproval />
                </Route>
                <Route exact path={virtualCardsRequestsUrl()}>
                  <VirtualCardsNeedsApproval />
                </Route>
                <Route path={'/'}>
                  <NotFoundBackgroundPageContent />
                </Route>
              </Switch>
            </FamilyLayout>
          </Route>
          <Route exact path={virtualCardsUrl()}>
            <Rdrct
              to={
                hasAccessToAllCards
                  ? virtualCardsRouteTree.family.redirectPrimaryTo
                  : virtualCardsRouteTree.family.redirectFallbackTo
              }
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
    />
  )
}

export default VirtualCardsFamilyPage
